import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faUserPen, faEye, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import debounce from "lodash/debounce"; // You'll need to install lodash: npm install lodash
import { viewerDisabledProps } from "../utils/permissions";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [employeeName, setEmployeeName] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [initialEmployees, setInitialEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Debounced search function to prevent too many API calls
  const debouncedSearch = useCallback(
    debounce((name, code, mobile) => {
      handleSearchDebounced(name, code, mobile);
    }, 500), // 500ms delay
    []
  );

  useEffect(() => {
    if (employeeName.trim() !== '' || employeeCode.trim() !== '' || mobileNumber.trim() !== '') {
      setIsSearching(true);
      debouncedSearch(employeeName, employeeCode, mobileNumber);
    } else {
      // If all search fields are empty, show all employees
      setEmployees(initialEmployees);
      setCurrentPage(1);
      setIsSearching(false);
    }
  }, [employeeName, employeeCode, mobileNumber]);

  const fetchEmployees = async () => {
    try {
      setIsSearching(true);
      const response = await axios.get(
        "https://ims-attendanceback.indevconsultancy.in/api/register-employee"
      );
      if (response.data.status) {
        setEmployees(response.data.employees);
        setInitialEmployees(response.data.employees);
      }
      setIsSearching(false);
    } catch (error) {
      console.error("Error fetching employee data:", error);
      setIsSearching(false);
    }
  };

  const handleSearchDebounced = (name, code, mobile) => {
    let filtered = [...initialEmployees];
    
    // Filter by employee name (case-insensitive partial match)
    if (name.trim() !== '') {
      filtered = filtered.filter(emp => 
        emp.emp_name && emp.emp_name.toLowerCase().includes(name.toLowerCase().trim())
      );
    }
    
    // Filter by employee code (exact match or partial)
    if (code.trim() !== '') {
      filtered = filtered.filter(emp => 
        emp.employee_code && emp.employee_code.toLowerCase().includes(code.toLowerCase().trim())
      );
    }
    
    // Filter by mobile number (partial match)
    if (mobile.trim() !== '') {
      filtered = filtered.filter(emp => 
        emp.emp_mobile_no && emp.emp_mobile_no.includes(mobile.trim())
      );
    }
    
    setEmployees(filtered);
    setCurrentPage(1);
    setIsSearching(false);
  };

  // Manual search function (if you still want a search button)
  const handleSearch = () => {
    setIsSearching(true);
    let filtered = [...initialEmployees];
    
    if (employeeName.trim() !== '') {
      filtered = filtered.filter(emp => 
        emp.emp_name && emp.emp_name.toLowerCase().includes(employeeName.toLowerCase().trim())
      );
    }
    
    if (employeeCode.trim() !== '') {
      filtered = filtered.filter(emp => 
        emp.employee_code && emp.employee_code.toLowerCase().includes(employeeCode.toLowerCase().trim())
      );
    }
    
    if (mobileNumber.trim() !== '') {
      filtered = filtered.filter(emp => 
        emp.emp_mobile_no && emp.emp_mobile_no.includes(mobileNumber.trim())
      );
    }
    
    setEmployees(filtered);
    setCurrentPage(1);
    setIsSearching(false);
  };

  const handleReset = () => {
    setEmployeeName('');
    setEmployeeCode('');
    setMobileNumber('');
    setEmployees(initialEmployees);
    setCurrentPage(1);
    setIsSearching(false);
  };

  const handleClearName = () => {
    setEmployeeName('');
  };

  const handleClearCode = () => {
    setEmployeeCode('');
  };

  const handleClearMobile = () => {
    setMobileNumber('');
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        const response = await axios.delete(
          "https://ims-attendanceback.indevconsultancy.in/api/register-employee",
          {
            data: { empdetailsid: id },
            headers: { "Content-Type": "application/json" }
          }
        );

        if (response.data.status) {
          alert("Employee deleted successfully!");
          const updated = employees.filter((emp) => emp.empdetailsid !== id);
          setEmployees(updated);
          setInitialEmployees(updated);
        } else {
          alert("Failed to delete employee!");
        }
      } catch (error) {
        console.error("Error deleting employee:", error);
        alert("Something went wrong while deleting!");
      }
    }
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = employees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(employees.length / itemsPerPage);

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="px-3 py-3 bg-cream" style={{ minHeight: "100%" }}>
      <div className="row align-items-center justify-content-between py-2">
        <div className="col-md-6 col-6">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb justify-content-start mb-0">
              <li className="breadcrumb-item">Employee List</li>
            </ol>
          </nav>
        </div>
        <div className="col-md-6 col-6 text-end">
          <span className="">Kitchen List</span>
        </div>
      </div>
      
      <div className="card">
        <div className="card-header bg-skyBlue text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Employee List</h5>
          {isSearching && (
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm text-white me-2" role="status">
                <span className="visually-hidden">Searching...</span>
              </div>
              <small>Searching...</small>
            </div>
          )}
        </div>
        
        <div className="card-body">
          {/* Search Filters */}
          <div className="d-flex flex-md-row flex-column align-items-stretch gap-2 mb-3">
            <div className="flex-grow-1 flex-basis-0 position-relative">
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Employee Name"
                  value={employeeName}
                  onChange={(e) => setEmployeeName(e.target.value)}
                  autoFocus
                />
                {employeeName && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={handleClearName}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-grow-1 flex-basis-0 position-relative">
              <div className="input-group">
                <span className="input-group-text bg-light">
                  #
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Employee Code"
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                />
                {employeeCode && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={handleClearCode}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-grow-1 flex-basis-0 position-relative">
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <i className="fas fa-phone"></i>
                </span>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Search by Mobile Number"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                />
                {mobileNumber && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={handleClearMobile}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-shrink-0">
              <button 
                className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                onClick={handleSearch}
                disabled={isSearching}
              >
                <FontAwesomeIcon icon={faSearch} className="me-2" />
                Search
              </button>
            </div>

            <div className="flex-shrink-0">
              <button 
                className="btn btn-secondary w-100"
                onClick={handleReset}
                disabled={isSearching}
              >
                Reset All
              </button>
            </div>
          </div>

          {/* Employee Count and Add Button */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h6 className="mb-0">
                Total Employees: <strong>{employees.length}</strong>
                {employeeName || employeeCode || mobileNumber ? (
                  <span className="text-muted ms-2">
                    (Filtered from {initialEmployees.length})
                  </span>
                ) : null}
              </h6>
              {employeeName && (
                <small className="text-primary d-block">
                  Searching for: "{employeeName}"
                </small>
              )}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-success"
                onClick={() => navigate("/add-employee-bulk")}
                {...viewerDisabledProps()}
              >
                Bulk Upload
              </button>
              <button
                className="btn btn-primary bg-skyBlue"
                onClick={() => navigate("/employeeForm")}
              >
                Add Employee
              </button>
            </div>
          </div>

          {/* Employees Table */}
          <div className="table-responsive">
            <table className="table table-bordered table-striped text-center">
              <thead className="table-light">
                <tr>
                  <th>S.No.</th>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Father / Husband Name</th>
                  <th>Mobile Number</th>
                  <th>Date of Joining</th>
                  <th>Date of Exit</th>
                  <th>Gender</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      {isSearching ? (
                        <div className="text-center py-3">
                          <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                          Searching employees...
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          No employees found. 
                          {(employeeName || employeeCode || mobileNumber) && " Try changing your search criteria."}
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  currentItems.map((emp, index) => (
                    <tr key={emp.empdetailsid || emp.id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{emp.employee_code || "-"}</td>
                      <td>
                        {employeeName && emp.emp_name ? (
                          <span>
                            {emp.emp_name.split(new RegExp(`(${employeeName})`, 'gi')).map((part, i) => 
                              part.toLowerCase() === employeeName.toLowerCase() ? 
                                <mark key={i} className="bg-warning px-1">{part}</mark> : 
                                part
                            )}
                          </span>
                        ) : (
                          emp.emp_name || "-"
                        )}
                      </td>
                      <td>{emp.father_name || "-"}</td>
                      <td>{emp.emp_mobile_no || "-"}</td>
                      <td>{formatDate(emp.date_of_joining)}</td>
                      <td>{formatDate(emp.date_of_exiting)}</td>
                      <td>{emp.gender || "-"}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-warning me-1 bg-orange text-white"
                          onClick={() =>
                            navigate(`/editemploye/${emp.empdetailsid}`, {
                              state: { employee: emp },
                            })
                          }
                          {...viewerDisabledProps()}
                        >
                          <FontAwesomeIcon icon={faUserPen} />
                        </button>
                        <button
                          className="btn btn-sm me-1 btn-danger"
                          onClick={() => handleDelete(emp.empdetailsid)}
                          {...viewerDisabledProps()}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                        <button
                          className="btn btn-sm btn-info text-white"
                          onClick={() =>
                            navigate(`/employee-details/${emp.empdetailsid}`, {
                              state: { employee: emp },
                            })
                          }
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button 
                          className="btn btn-sm btn-success text-white" 
                          onClick={() => {
                            const now = new Date();
                            const monthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                            navigate(`/leave-credits?employee_id=${emp.empdetailsid}&month_year=${monthYear}`);
                          }}
                          title="View Leave Credits"
                        >
                          Credits
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="mt-3 d-flex justify-content-center" aria-label="Pagination">
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => changePage(currentPage - 1)}>
                      Previous
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                      <button className="page-link" onClick={() => changePage(i + 1)}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => changePage(currentPage + 1)}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
        
        <div className="card-footer text-muted text-center small">
          Technology Partner: Indev Consultancy ©2025
        </div>
      </div>
    </div>
  );
};

export default EmployeeList;